"use client";

import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";

export interface TocItem {
  id: string;
  title: string;
  level?: number;
}

interface DocsTableOfContentsProps {
  items: TocItem[];
  activeId?: string;
  className?: string;
  onItemClick?: (id: string) => void;
}

export function DocsTableOfContents({
  items,
  activeId,
  className,
  onItemClick,
}: DocsTableOfContentsProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();

    const element = document.getElementById(id);
    if (element) {
      onItemClick?.(id);

      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <nav className={cn("", className)} aria-label="On this page">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-text-muted" />
        <h2 className="text-sm font-medium text-text">On this page</h2>
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li
            key={item.id}
            style={{
              paddingLeft: item.level && item.level > 2 ? `${(item.level - 2) * 12}px` : undefined,
            }}
          >
            <a
              href={`#${item.id}`}
              onClick={(e) => handleClick(e, item.id)}
              className={cn(
                "block text-sm py-1.5 transition-colors border-l-2 pl-3 -ml-[2px]",
                activeId === item.id
                  ? "text-primary border-primary font-medium"
                  : "text-text-muted border-border/50 hover:text-text hover:border-border"
              )}
            >
              {item.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
